/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  НОРМАЛИЗАЦИЯ ОШИБОК: что угодно → `ApiError`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Из `lib/http.ts` наружу не выходит ни одна не-`ApiError`. Стор, композаблы и
 * вью ловят ровно один тип и ветвятся ровно по одному полю — `reason`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ГЛАВНОЕ ПРАВИЛО: `message` С ПРОВОДА ПОЛЬЗОВАТЕЛЮ НЕ ПОКАЗЫВАЕТСЯ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Конверт кратоса — `{code, reason, message, metadata}` (см. `types/api.ts`).
 * Его `message` — **английский текст для разработчика**: «failed to resolve qr:
 * pq: connection reset by peer». Показать его регистратору значит одновременно
 * испортить UX и слить внутренности схемы.
 *
 * Поэтому `ApiError.message` (то, что уедет в тост или на экран) собирается
 * **здесь**, из `reason`, а серверный текст откладывается в `devMessage` — для
 * консоли и багрепортов. Цепочка фолбэков:
 *
 *     спец-случай (`RATE_LIMITED.retry_after_s`)
 *       → текст по `reason`
 *         → текст по HTTP-статусу   (сюда попадают ответы не от кратоса:
 *                                    502 от nginx, 500 от прокси Vite)
 *           → общий текст
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ СЛОВАРЬ ТИПИЗИРОВАН ПЕРЕСЕЧЕНИЕМ
 * ───────────────────────────────────────────────────────────────────────────
 *
 *     Record<ErrorReason, string> & Record<string, string>
 *
 * Левая половина делает покрытие **обязательным**: забыть значение из
 * `ERROR_REASONS` нельзя, компилятор не даст. Правая — разрешает лишние ключи
 * и произвольный `reason` при чтении: фронт старше или новее бэкенда ровно на
 * один деплой, и незнакомая причина обязана давать общий текст, а не
 * `undefined` посреди экрана. Тотальность обеспечена типом, устойчивость —
 * фолбэком; ни одно из двух не заменяет другое.
 *
 * Два ключа здесь заслуживают отдельного внимания — на них ветвится экран
 * сканирования: `PARTICIPANT_NOT_FOUND` (QR разобран, участника нет) и
 * `INVALID_QR_PAYLOAD` (это вообще не наш QR — чужая ссылка, штрихкод, Wi-Fi).
 * Разница между ними для регистратора принципиальна: в первом случае звать
 * администратора, во втором — просто навести камеру на нужный бейдж.
 */

import axios from 'axios'

import type { ErrorReason, ErrorResponse } from '@/types/api'

/* ═════════════════════════════════════════════════════════════════════════
   1. ЛОКАЛЬНЫЕ `reason`, КОТОРЫХ НЕТ НА ПРОВОДЕ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Причины, которые придумывает клиент: сервер до них не доехал или не ответил.
 * Живут в том же пространстве `reason`, чтобы у вызывающего кода был **один**
 * `switch`, а не «сначала проверь, не сетевая ли это ошибка».
 *
 * • `NETWORK`          — ответа нет вообще (сервер лежит, Wi-Fi зала моргнул, DNS).
 * • `TIMEOUT`          — не уложились в `timeout` axios.
 * • `CANCELED`         — `AbortController.abort()`; **не ошибка**, см. текст ниже.
 * • `NO_REFRESH_TOKEN` — refresh нечем делать: в хранилище пусто.
 */
export const LOCAL_ERROR_REASONS = ['NETWORK', 'TIMEOUT', 'CANCELED', 'NO_REFRESH_TOKEN'] as const
export type LocalErrorReason = typeof LOCAL_ERROR_REASONS[number]

/* ═════════════════════════════════════════════════════════════════════════
   2. СЛОВАРИ
   ═════════════════════════════════════════════════════════════════════════ */

/** `reason` → текст для пользователя. О типе — см. шапку файла. */
const REASON_TEXT: Record<ErrorReason, string> & Record<string, string> = {
  /* — значения контракта, на которые сканер ветвится (`ERROR_REASONS`) — */
  UNKNOWN: 'Внутренняя ошибка сервера. Попробуйте позже.',
  VALIDATION_FAILED: 'Проверьте правильность заполнения полей.',
  INVALID_CREDENTIALS: 'Неверный email или пароль.',
  ACCESS_TOKEN_MISSING: 'Требуется вход в систему.',
  ACCESS_TOKEN_INVALID: 'Сессия недействительна. Войдите заново.',
  ACCESS_TOKEN_EXPIRED: 'Сессия истекла. Войдите заново.',
  REFRESH_TOKEN_INVALID: 'Сессия недействительна. Войдите заново.',
  REFRESH_TOKEN_EXPIRED: 'Срок сессии истёк. Войдите заново.',
  REFRESH_TOKEN_REUSED: 'Сессия завершена: токен доступа был использован повторно. Войдите заново.',
  SESSION_REVOKED: 'Сессия завершена. Войдите заново.',
  FORBIDDEN: 'Недостаточно прав для этого действия.',
  USER_DISABLED: 'Учётная запись отключена. Обратитесь к администратору.',
  RATE_LIMITED: 'Слишком много попыток. Попробуйте позже.',

  /* — две причины, ради которых экран сканирования и различает ошибки — */
  PARTICIPANT_NOT_FOUND: 'Участник не найден.',
  INVALID_QR_PAYLOAD: 'Это не код участника.',

  /* — не нормализованные бэкендом: middleware кратоса выше нашего кода — */
  VALIDATOR: 'Проверьте правильность заполнения полей.',
  CODEC: 'Некорректный формат запроса.',
  UNAUTHORIZED: 'Требуется вход в систему.',

  /* — чисто клиентские — */
  NETWORK: 'Сервер недоступен. Проверьте связь и попробуйте ещё раз.',
  TIMEOUT: 'Сервер не ответил вовремя. Попробуйте ещё раз.',
  NO_REFRESH_TOKEN: 'Сессия не найдена. Войдите заново.',

  /**
   * Пустая строка — не забытый перевод, а осознанное решение.
   *
   * Отменённый запрос (`AbortController.abort()`) — штатная часть работы
   * сканера: камера успевает распознать один и тот же код дважды, и второй
   * разбор гасит первый. Показать здесь текст значит мигать красным на ровном
   * месте. Вызывающий код проверяет `if (!error.message) return` — либо
   * `error.isCanceled`, если хочет ветвиться явно.
   */
  CANCELED: '',
}

/**
 * HTTP-статус → текст. Второй эшелон: сюда попадает всё, что **не** конверт
 * кратоса, — 502/504 от nginx, 500 от прокси Vite при мёртвом бэкенде,
 * страница-заглушка гостевого Wi-Fi с captive portal.
 */
const STATUS_TEXT: Record<number, string> = {
  400: 'Некорректный запрос.',
  401: 'Требуется вход в систему.',
  403: 'Недостаточно прав для этого действия.',
  404: 'Запрошенные данные не найдены.',
  405: 'Операция не поддерживается.',
  409: 'Данные уже изменились. Обновите страницу и повторите.',
  413: 'Слишком большой запрос.',
  429: 'Слишком много запросов. Попробуйте позже.',
  500: 'Внутренняя ошибка сервера.',
  502: 'Сервер временно недоступен. Попробуйте позже.',
  503: 'Сервер временно недоступен. Попробуйте позже.',
  504: 'Сервер не ответил вовремя. Попробуйте ещё раз.',
}

const GENERIC_TEXT = 'Что-то пошло не так. Попробуйте ещё раз.'

/* ═════════════════════════════════════════════════════════════════════════
   3. СБОРКА ТЕКСТА
   ═════════════════════════════════════════════════════════════════════════ */

/** Секунды → «Повторите через 45 с» / «Повторите через 3 минуты». */
function formatRetryAfter(seconds: number): string {
  if (seconds < 60) return `Повторите через ${seconds} с.`
  const minutes = Math.ceil(seconds / 60)
  const noun = minutes % 10 === 1 && minutes % 100 !== 11 ? 'минуту' : 'минут'
  return `Повторите через ${minutes} ${noun}.`
}

/**
 * Спец-случаи, где `metadata` делает текст точнее. Возвращает `null`, если
 * уточнять нечем, — и тогда работает обычная цепочка фолбэков.
 */
function specificText(reason: string, metadata: Record<string, string>): string | null {
  if (reason === 'RATE_LIMITED') {
    // Значения `metadata` — всегда строки, даже числовые (см. `types/api.ts`).
    const seconds = Number(metadata.retry_after_s)
    if (Number.isFinite(seconds) && seconds > 0) {
      return `${REASON_TEXT.RATE_LIMITED} ${formatRetryAfter(Math.ceil(seconds))}`
    }
  }

  return null
}

/** Полная цепочка: спец-случай → reason → статус → общий текст. */
function describe(reason: string, status: number, metadata: Record<string, string>): string {
  const specific = specificText(reason, metadata)
  if (specific !== null) return specific

  // `??`, а не `||`: у `CANCELED` текст — пустая строка, и она значащая.
  return REASON_TEXT[reason] ?? STATUS_TEXT[status] ?? GENERIC_TEXT
}

/* ═════════════════════════════════════════════════════════════════════════
   4. КЛАСС
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Единственный тип ошибки, который видит приложение.
 *
 * Поля присваиваются в теле конструктора, а не через параметры-свойства:
 * `erasableSyntaxOnly: true` в `tsconfig.app.json` запрещает конструкцию
 * `constructor(public status: number)` — у неё есть рантайм-семантика,
 * несводимая к стиранию типов.
 */
export class ApiError extends Error {
  /** HTTP-статус. `0` — ответа не было вообще (`NETWORK` / `TIMEOUT` / `CANCELED`). */
  status: number

  /** Ключ ветвления и локализации. Не обязан входить в `ErrorReason` — см. шапку. */
  reason: string

  /** `map<string,string>` из конверта. Всегда объект, в худшем случае пустой. */
  metadata: Record<string, string>

  /**
   * Серверный `message` — **английский, для разработчика**.
   * Для логов и багрепортов; в интерфейс не попадает никогда.
   */
  devMessage: string

  constructor(
    status: number,
    reason: string,
    metadata: Record<string, string> = {},
    devMessage = '',
  ) {
    super(describe(reason, status, metadata))

    this.name = 'ApiError'
    this.status = status
    this.reason = reason
    this.metadata = metadata
    this.devMessage = devMessage
  }

  /**
   * Запрос отменён вызывающим кодом. Не ошибка: `message` пустой, показывать
   * нечего. Геттер — чтобы вызывающий код не сравнивал строки руками.
   */
  get isCanceled(): boolean {
    return this.reason === 'CANCELED'
  }
}

/* ═════════════════════════════════════════════════════════════════════════
   5. НОРМАЛИЗАЦИЯ
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Похоже ли `data` на конверт ошибки кратоса.
 *
 * Проверяем именно `reason`, а не `code`: `code` есть и у половины чужих
 * JSON-ответов, а `reason: string` — подпись конкретно нашего конверта.
 */
function isErrorEnvelope(data: unknown): data is Partial<ErrorResponse> {
  return typeof data === 'object' && data !== null && typeof (data as ErrorResponse).reason === 'string'
}

/**
 * `metadata` в конверте — protobuf `map`, то есть по контракту присутствует
 * всегда (`{}`, когда пусто). Здесь мы всё равно приводим её к объекту: этот
 * код обрабатывает и ответы, которые кратос не писал (502 от nginx, JSON
 * от captive portal), и ломаться на них он не имеет права.
 */
function toMetadata(value: unknown): Record<string, string> {
  if (typeof value !== 'object' || value === null) return {}

  const result: Record<string, string> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'string') result[key] = item
    else if (typeof item === 'number' || typeof item === 'boolean') result[key] = String(item)
  }
  return result
}

/**
 * Что угодно → `ApiError`. Идемпотентна: `normalizeError(apiError) === apiError`.
 *
 * Порядок веток — от самого специфичного к самому общему, и он значим:
 * у отменённого запроса `response` тоже отсутствует, так что проверка
 * `ERR_CANCELED` обязана идти **до** проверки на сетевую ошибку, иначе
 * отменённый разбор QR давал бы «Сервер недоступен».
 */
export function normalizeError(err: unknown): ApiError {
  if (err instanceof ApiError) return err

  if (axios.isAxiosError(err)) {
    // 1. Отмена — не ошибка. Должна идти первой: `response` у неё тоже нет.
    if (err.code === 'ERR_CANCELED') {
      return new ApiError(0, 'CANCELED', {}, err.message)
    }

    // 2. Не уложились в `timeout`. `ETIMEDOUT` — тот же случай под Node.
    if (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT') {
      return new ApiError(0, 'TIMEOUT', {}, err.message)
    }

    const response = err.response

    // 3. Ответа не было вообще: сервер лежит, обрыв Wi-Fi, DNS, зарубленный preflight.
    if (!response) {
      return new ApiError(0, 'NETWORK', {}, err.message)
    }

    // 4. Ответ есть. Конверт кратоса — если это он.
    const data: unknown = response.data
    if (isErrorEnvelope(data)) {
      return new ApiError(
        // `code` в конверте равен HTTP-статусу, но авторитет — сам статус:
        // на пути к браузеру ответ мог переписать прокси.
        response.status,
        data.reason ?? 'UNKNOWN',
        toMetadata(data.metadata),
        typeof data.message === 'string' ? data.message : '',
      )
    }

    // 5. Ответ не от кратоса. `reason` пустой — намеренно: сказать «UNKNOWN»
    //    значило бы приписать серверу причину, которой он не называл, и
    //    перебить более точный текст по статусу (502 → «Сервер временно
    //    недоступен» вместо «Внутренняя ошибка сервера»).
    return new ApiError(response.status, '', {}, typeof data === 'string' ? data : err.message)
  }

  // 6. Не axios: `AbortError` от голого fetch, TypeError из нашего же кода, что угодно.
  if (err instanceof DOMException && err.name === 'AbortError') {
    return new ApiError(0, 'CANCELED', {}, err.message)
  }

  if (err instanceof Error) {
    return new ApiError(0, 'UNKNOWN', {}, err.message)
  }

  return new ApiError(0, 'UNKNOWN', {}, String(err))
}
